import { Injectable } from "@angular/core";
import { ApiService } from "app/core/api/api.service";
import { GrowthBookService } from "app/shared/services/growthbook.service";
import { IOperationTypes } from "app/modules/admin/v2/operation/interfaces";
import { IOutputPayableError } from "../interfaces/output-payable-error.interface";
import { ICreatedPayable } from "../interfaces/created-payable.interface";
import { IXlsxValidationResponse } from "../interfaces/xlsx-validation-responseinterface";
import { ReceipientBankAccount } from "../types/receipiente-bank-account.interface";
import { UnoperatedPayablesSelection } from "./components/repurchase-payables-modal/interfaces/unoperated-payables";
import { featureFlags } from "../constants";

export interface RepurchasePreviewResponse {
    disbursement: number;
    repurchaseValue: number;
    netDisbursement: number;
    breakdown: {
        payableId: number;
        baseValue: number;
        repurchaseValue: number;
        fine: number;
        lateFee: number;
        total: number;
    }[];
}

@Injectable({
    providedIn: "root",
})
export class CreateOperationService {
    constructor(
        private http: ApiService,
        private readonly growthBookService: GrowthBookService,
    ) {}

    async getOperationTypes(): Promise<Array<IOperationTypes>> {
        const response = await this.http.get("crm", "/v2/operation/types").toPromise();

        return response.data;
    }

    async getXMLInconsistencies(files: File[]) {
        try {
            const { body } = await this.http.makeFileRequest("bff", "/payable/xml", files, "files").toPromise<any>();
            return body as IOutputPayableError;
        } catch {
            throw new Error("Ocorreu um erro ao comunicar com o servidor.");
        }
    }

    async getXLSXInconsistencies(file: File) {
        try {
            const { body } = await this.http.makeFileRequest("bff", "/payable/xlsx", [file], "file").toPromise<any>();

            return body;
        } catch {
            throw new Error("Ocorreu um erro ao comunicar com o servidor.");
        }
    }

    async createPayablesFromXLSXFormatted(data: IXlsxValidationResponse) {
        try {
            return this.http.post("crm", "/v2/payables/by-xlsx", data).toPromise<ICreatedPayable[]>();
        } catch {
            throw new Error("Ocorreu um erro ao comunicar com o servidor.");
        }
    }

    async getFundingAmount(): Promise<{
        amount: number;
        fundingLimit: number;
        fundingInterestRate: number;
        fundingId: string;
    } | null> {
        try {
            const fundingResponse = await this.http.get("oracle", "/funding/available").toPromise();

            return {
                ...fundingResponse,
                amount: fundingResponse.amount < 0 ? 0 : fundingResponse.amount,
            };
        } catch (error) {
            return Promise.resolve(null);
        }
    }

    async getAccountBalance(minibankId?: number): Promise<number | null> {
        const isCheckingAccountEnabled = this.growthBookService.isFeatureEnabled(
            featureFlags.ENABLE_CHECKING_ACCOUNT,
        );

        try {
            if (isCheckingAccountEnabled && minibankId != null && minibankId > 0) {
                const response = await this.http
                    .get("oracle", `/bifrost/balance/${minibankId}`)
                    .toPromise<{ balance?: number }>();
                return typeof response?.balance === "number" ? response.balance : 0;
            }

            const { availableBalance } = await this.http
                .get("oracle", "/bff-front-bankme/operation/available-balance")
                .toPromise<{ availableBalance?: number }>();
            return typeof availableBalance === "number" ? availableBalance : null;
        } catch {
            return null;
        }
    }

    async getAssignorBankAccounts(assignorId: number): Promise<ReceipientBankAccount[]> {
        try {
            return this.http.get("oracle", `/bff-front-bankme/assignors/${assignorId}/bank-accounts`).toPromise();
        } catch {
            return null;
        }
    }

    async createRepurchasedPayables(
        data: UnoperatedPayablesSelection & { operationId: string; disbursement: number },
    ): Promise<{ created: number; error: number }> {
        try {
            await this.http.post("oracle", "/bff-front-bankme/payable/repurchase", data).toPromise();
        } catch {
            return null;
        }
    }

    async previewRepurchaseDisbursement(
        data: Omit<UnoperatedPayablesSelection, "payables"> & {
            payables: UnoperatedPayablesSelection["payables"];
            disbursement: number;
        },
    ): Promise<RepurchasePreviewResponse | null> {
        try {
            return this.http
                .post("oracle", "/bff-front-bankme/payable/repurchase/preview", data)
                .toPromise();
        } catch {
            return null;
        }
    }
}
